import React, { useEffect, useRef } from 'react';
import { Figma, Palette, Layout, Code, Smartphone, Zap } from 'lucide-react';
import RevealText from '../motion/RevealText';
import '../../styles/about.css';

// Les deux versants du profil, plutôt qu'une liste indifférenciée de six outils.
const metiers = [
  {
    titre: 'Je conçois',
    outils: [
      { icon: Figma, name: 'Figma', description: 'Design & prototypage' },
      { icon: Palette, name: 'Design UI', description: 'Design visuel' },
      { icon: Layout, name: 'Recherche UX', description: 'Expérience utilisateur' }
    ]
  },
  {
    titre: "J'intègre",
    outils: [
      { icon: Code, name: 'HTML / CSS / JS', description: 'Dév. front-end' },
      { icon: Smartphone, name: 'Responsive', description: 'Mobile-first' },
      { icon: Zap, name: 'WordPress', description: 'Développement CMS' }
    ]
  }
];

const About = () => {
  const portraitRef = useRef(null);

  // Le portrait sort du noir et prend ses couleurs à mesure qu'il monte dans
  // la fenêtre. La valeur par défaut des variables est l'état « révélé » :
  // sans JavaScript, ou en mouvement réduit, la photo s'affiche normalement.
  useEffect(() => {
    const el = portraitRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let frame = 0;
    const peindre = () => {
      frame = 0;
      const haut = el.getBoundingClientRect().top;
      const vh = window.innerHeight;
      const t = Math.min(1, Math.max(0, (vh * 0.9 - haut) / (vh * 0.6)));
      el.style.setProperty('--revele', String(Math.round(t * 100) / 100));
      el.style.setProperty('--derive', `${Math.round((1 - t) * 24)}px`);
    };

    const auDefilement = () => {
      if (!frame) frame = requestAnimationFrame(peindre);
    };

    peindre();
    window.addEventListener('scroll', auDefilement, { passive: true });
    window.addEventListener('resize', auDefilement);

    return () => {
      window.removeEventListener('scroll', auDefilement);
      window.removeEventListener('resize', auDefilement);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="section about" id="about">
      <div className="shell">
        <div className="section-head">
          <h2>Du code au design, et retour</h2>
        </div>

        <div className="about-grid">
          <div className="about-sticky">
            <figure className="about-portrait" ref={portraitRef}>
              <img
                src="/photo-kevin.webp"
                alt="Kévin Gelineau, UX/UI Designer basé à Angers"
                className="about-photo"
              />
              <span className="about-halo" aria-hidden="true" />
              <span className="about-photo-rule" aria-hidden="true" />
            </figure>
          </div>

          <div className="about-body">
            <RevealText className="about-para">
              Designer UX/UI avec un profil technique issu d'un BTS SIO SLAM, je conçois des
              interfaces qui parlent aux utilisateurs <em>et</em> aux développeurs. Je travaille
              sur Figma pour le design — recherche utilisateur, wireframes, maquettes
              haute-fidélité — et j'intègre moi-même avec WordPress, Elementor, et
              progressivement avec des outils comme Claude, Google Stitch et Emergent.
            </RevealText>

            <RevealText className="about-para">
              Mon parcours hybride me permet de penser en composants, d'anticiper les
              contraintes d'intégration, et de livrer des maquettes prêtes à être développées
              sans friction. Actuellement en alternance chez iStudio, disponible pour des
              missions freelance.
            </RevealText>

            <div className="metiers">
              {metiers.map((metier, m) => (
                <section className="metier" key={metier.titre}>
                  <h3 className="metier-titre">{metier.titre}</h3>
                  <ul className="skills">
                    {metier.outils.map((outil, i) => {
                      const Icon = outil.icon;
                      return (
                        <li
                          className="skill"
                          key={outil.name}
                        >
                          <Icon size={18} className="skill-icon" aria-hidden="true" />
                          <span className="skill-name">{outil.name}</span>
                          <span className="skill-desc">{outil.description}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
